import { useRef, useState } from 'react'
import { Image as ImageIcon, Upload } from 'lucide-react'
import { apiService } from '@/services/api'
import { comprimirImagen } from '@/shared/lib/imagenes'
import toast from 'react-hot-toast'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

/**
 * Tres ranuras para subir fotos de evidencia.
 *
 * Vivía dentro del formulario de baja de `HerramientaForm`, que pasa de tres
 * mil líneas. Se saca porque el cierre de una incidencia también pide evidencia
 * cuando la herramienta se da de baja (ALM-01), y duplicar la maquinaria de
 * subida —refs, estado por ranura, compresión, manejo de error— era copiar cien
 * líneas para el mismo comportamiento.
 *
 * Sube al vuelo y devuelve las URL ya cargadas: mientras una foto va en camino
 * se muestra la vista previa local y se avisa hacia arriba con `subiendo`, para
 * que quien lo use pueda bloquear su botón de guardar y no mande el formulario
 * con una URL a medias.
 */
export function SubirEvidencias({
  urls,
  onChange,
  onSubiendoChange,
}: {
  /** URL ya cargadas, una por ranura. `null` en las vacías. */
  urls: (string | null)[]
  onChange: (urls: (string | null)[]) => void
  onSubiendoChange?: (subiendo: boolean) => void
}) {
  const [subiendo, setSubiendo] = useState<boolean[]>([false, false, false])

  const ref0 = useRef<HTMLInputElement>(null)
  const ref1 = useRef<HTMLInputElement>(null)
  const ref2 = useRef<HTMLInputElement>(null)
  const refs = [ref0, ref1, ref2]

  const marcarSubiendo = (indice: number, valor: boolean) => {
    const siguiente = subiendo.map((v, i) => (i === indice ? valor : v))
    setSubiendo(siguiente)
    onSubiendoChange?.(siguiente.some(Boolean))
  }

  const alElegirArchivo =
    (indice: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      const archivo = e.target.files?.[0]
      if (!archivo) return
      // Se limpia el input para que volver a elegir la misma foto dispare el
      // cambio otra vez.
      e.target.value = ''

      const vistaPrevia = URL.createObjectURL(archivo)
      onChange(urls.map((v, i) => (i === indice ? vistaPrevia : v)))
      marcarSubiendo(indice, true)

      try {
        const cuerpo = new globalThis.FormData()
        cuerpo.append('file', await comprimirImagen(archivo))
        const res = await apiService.request<{ url: string }>('/almacen/upload/foto', {
          method: 'POST',
          body: cuerpo,
        })
        URL.revokeObjectURL(vistaPrevia)
        onChange(urls.map((v, i) => (i === indice ? res.url : v)))
      } catch {
        URL.revokeObjectURL(vistaPrevia)
        onChange(urls.map((v, i) => (i === indice ? null : v)))
        toast.error('Error al subir la foto. Intenta de nuevo.')
      } finally {
        marcarSubiendo(indice, false)
      }
    }

  return (
    <div className="flex flex-col gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-lg border px-4 py-2.5"
          style={{ borderColor: '#E0E0E0' }}
        >
          <input
            ref={refs[i]}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={alElegirArchivo(i)}
          />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {subiendo[i] ? (
              <div className="w-5 h-5 rounded-full border-2 border-[#315FB9] border-t-transparent animate-spin flex-shrink-0" />
            ) : urls[i] ? (
              <img
                src={urls[i]!}
                alt=""
                className="w-8 h-8 rounded object-cover flex-shrink-0"
              />
            ) : (
              <ImageIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
            )}
            <span className="text-sm text-gray-500 truncate">
              {subiendo[i] ? 'Subiendo…' : urls[i] ? 'Imagen cargada' : 'Cargar imagen'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => refs[i].current?.click()}
            disabled={subiendo[i]}
            className={estiloDeBoton('primario', 'flex-shrink-0')}
          >
            <Upload className="w-4 h-4" />
            Cargar
          </button>
        </div>
      ))}
    </div>
  )
}

export default SubirEvidencias
